import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { AppMode } from '../../stores/agencies.store';
import { useMyAgencies } from '../../stores/agencies.store';
import LoadingScreen from '../ui/LoadingScreen';
import DashboardPage from '../../pages/dashboard/DashboardPage';

// Staff and admins are few: their screens stay out of the main chunk (PWA precache limit).
const AgencyHomePage = lazy(() => import('../../pages/agency/AgencyHomePage'));
const AdminHomePage = lazy(() => import('../../pages/admin/AdminHomePage'));

/**
 * ADR-0036: the home is the agency's in agency mode, the admin's in admin mode
 * and the traveller's otherwise. Waits for the answer so staff don't see the
 * traveller's flash.
 */
export function HomeRoute() {
  const mine = useMyAgencies();
  if (mine.status === 'loading') return <LoadingScreen />;
  if (mine.agency) return <Suspense fallback={<LoadingScreen />}><AgencyHomePage agency={mine.agency} /></Suspense>;
  if (mine.mode === 'admin') return <Suspense fallback={<LoadingScreen />}><AdminHomePage /></Suspense>;
  return <DashboardPage />;
}

/**
 * Screens of one mode: an account without it goes back to its home (and never
 * downloads them); one that has it is switched to it on arrival. Only on
 * arrival — switching mode from the menu while on one of these screens updates
 * the mode before React Router's navigation (a transition) lands, and a guard
 * reacting to the mode would switch straight back.
 */
function ModeOnly({ mode }: { mode: AppMode }) {
  const mine = useMyAgencies();
  const { key } = useLocation();
  const ready = mine.status === 'ready';
  const allowed = ready && mine.modes.includes(mode);

  useEffect(() => {
    if (allowed && mine.mode !== mode) mine.setMode(mode);
    // Deliberately not on `mine.mode`: see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  if (!ready) return <LoadingScreen />;
  return allowed ? <Outlet /> : <Navigate to="/dashboard" replace />;
}

/** ADR-0036: someone who works at no agency never reaches its screens, not even by URL. */
export function StaffOnly() {
  return <ModeOnly mode="agency" />;
}

/** Gromly's administration: only admins. The server checks every call anyway (`requireAdmin`). */
export function AdminOnly() {
  return <ModeOnly mode="admin" />;
}
